import { defineStore } from 'pinia';
import { computed, ref, shallowRef } from 'vue';
import type { RouteLocationRaw } from 'vue-router';

import { showFlash } from '@/alerts';
import { dashboardBffClient } from '@/api-client';
import type {
  ActionsListSummary,
  AppPoliciesMap,
  ClusterRestorePointAPI,
  DistributionList,
  K10Action,
  K10HealthCheck,
  KeylessAuth,
  Namespace,
  PolicyP,
  Report,
  RestorePointAPI,
  RestorePointContentAPI,
  SnapshotsSummary,
  UserInfo,
} from '@/api-client/api';
import type { TAtPath } from '@/api-client/exctract-types';
import { debug as logDebug, error as logError } from '@/logger';
import { defaultErrorHandler } from '@/services';
import { downloadBlob } from '@/utils/download';
import { sleep } from '@/utils/fetch-utils';

export interface MainStoreState {
  actionDetailsCache: TAtPath<K10Action, ['status', 'details']> | object;
  alertMessages: AlertMessage[];
  distributions: DistributionList;
  formPrefillData: object | null;
  healthStatus: K10HealthCheck | null;
  namespaces: Namespace[] | null;
  namespacesAllClusters: Namespace[] | null;
  permsDetected: DetectedPerms;
  userInfo: UserInfo;
  isUserInfoFetching: boolean;
  policies: PolicyP[] | null;
  policiesByApp: AppPoliciesMap;
  protectedObjectCache: Record<string, any>;
  reports: Report[];
  restorePointsCache: Record<string, any>;
  restorePointArtifactsCache: RestorePointArtifactCache | null;
  transformEditorCache: string;
  settings: Record<string, any>;
  summaryMetrics: SummaryMetrics;
  xhrEnabled: boolean;
  preventSidepanelClose: boolean;
  keylessAuth: KeylessAuth;
  isK10LogsPending: boolean;
}

type DetectedPerms = UserInfo['access'];
type RestorePointArtifactCache =
  | TAtPath<RestorePointAPI, ['status', 'restorePointDetails', 'artifacts']>
  | TAtPath<ClusterRestorePointAPI, ['status', 'clusterRestorePointDetails', 'artifacts']>
  | TAtPath<RestorePointContentAPI, ['status', 'restorePointContentDetails', 'artifacts']>;
type SummaryMetrics = { job: ActionsListSummary | object; snapshot: SnapshotsSummary | object };
export type AlertMessage = {
  id: string;
  level: 'info' | 'warning' | 'error';
  title: string;
  message: string;
  button?: {
    text: string;
    routeParams?: RouteLocationRaw;
    href?: string;
    dismiss?: boolean;
    classes?: string;
  };
  muted: boolean;
  firstSeen: string;
  lastSeen: string;
};

export const defaultMainStoreState = (): MainStoreState => {
  return {
    actionDetailsCache: {},
    alertMessages: [], // managed in alert-checks.js
    distributions: [],
    formPrefillData: null,
    healthStatus: null,
    namespaces: null, // central storage for namespaces list
    namespacesAllClusters: null,
    // User info and permissions.  Fetched in router.js
    permsDetected: { namespaces: { list: { allowed: false } } },
    userInfo: { user: {}, access: {}, primary: false, isPrimaryAdmin: false },
    isUserInfoFetching: true,
    policies: null, // Local policies (not global)
    policiesByApp: {},
    protectedObjectCache: {},
    reports: [],
    restorePointsCache: {}, // App cards
    restorePointArtifactsCache: null, // Used to hand off artifacts from RP detail to Test Transforms.
    transformEditorCache: '',
    settings: {},
    summaryMetrics: { job: {}, snapshot: {} },
    xhrEnabled: false,
    preventSidepanelClose: false,
    keylessAuth: {},
    isK10LogsPending: false,
  };
};

export const useMainStore = defineStore('main', () => {
  const defaults = defaultMainStoreState();

  const actionDetailsCache = ref<MainStoreState['actionDetailsCache']>(defaults.actionDetailsCache);
  const alertMessages = ref<MainStoreState['alertMessages']>(defaults.alertMessages);
  const distributions = ref<MainStoreState['distributions']>(defaults.distributions);
  const formPrefillData = ref<MainStoreState['formPrefillData']>(defaults.formPrefillData);
  const healthStatus = ref<MainStoreState['healthStatus']>(defaults.healthStatus);
  const namespaces = ref<MainStoreState['namespaces']>(defaults.namespaces);
  const namespacesAllClusters = ref<MainStoreState['namespacesAllClusters']>(
    defaults.namespacesAllClusters,
  );
  const permsDetected = ref<MainStoreState['permsDetected']>(defaults.permsDetected);
  const userInfo = ref<MainStoreState['userInfo']>(defaults.userInfo);
  const isUserInfoFetching = ref<MainStoreState['isUserInfoFetching']>(defaults.isUserInfoFetching);
  // shallowRef: lists are replaced wholesale on fetch — deep reactivity is wasted work
  const policies = shallowRef<MainStoreState['policies']>(defaults.policies);
  const reports = shallowRef<MainStoreState['reports']>(defaults.reports);
  const policiesByApp = ref<MainStoreState['policiesByApp']>(defaults.policiesByApp);
  const protectedObjectCache = ref<MainStoreState['protectedObjectCache']>(
    defaults.protectedObjectCache,
  );
  const restorePointsCache = ref<MainStoreState['restorePointsCache']>(defaults.restorePointsCache);
  const restorePointArtifactsCache = ref<MainStoreState['restorePointArtifactsCache']>(
    defaults.restorePointArtifactsCache,
  );
  const transformEditorCache = ref<MainStoreState['transformEditorCache']>(
    defaults.transformEditorCache,
  );
  const settings = ref<MainStoreState['settings']>(defaults.settings);
  const summaryMetrics = ref<MainStoreState['summaryMetrics']>(defaults.summaryMetrics);
  const xhrEnabled = ref<MainStoreState['xhrEnabled']>(defaults.xhrEnabled);
  const preventSidepanelClose = ref<MainStoreState['preventSidepanelClose']>(
    defaults.preventSidepanelClose,
  );
  const keylessAuth = ref<MainStoreState['keylessAuth']>(defaults.keylessAuth);
  const isK10LogsPending = ref<MainStoreState['isK10LogsPending']>(defaults.isK10LogsPending);

  const reportsIsEnabled = computed(() => {
    return (
      policies.value?.some((policy) => policy.metadata?.name === 'k10-system-reports-policy') ??
      false
    );
  });
  const azureManagedIdentityStatus = computed(() => {
    return keylessAuth.value.azure?.status;
  });

  function setUserPermissionValue(params: { object: string; verb: string; value: boolean }) {
    if (!permsDetected.value[params.object]) {
      permsDetected.value[params.object] = {};
    }
    if (!permsDetected.value[params.object][params.verb]) {
      permsDetected.value[params.object][params.verb] = { allowed: false };
    }
    permsDetected.value[params.object][params.verb].allowed = params.value;
  }

  async function getSetPoliciesByApp() {
    const { data } = await dashboardBffClient.appPolicies.listAppPolicies();

    policiesByApp.value = data.appPolicies!;
  }

  async function fetchUserPermissions(clusterId?: string) {
    try {
      isUserInfoFetching.value = true;
      const { data } = await dashboardBffClient.userInfo.userInfo({ clusterId });
      userInfo.value = data;
      logDebug('Fetched userInfo with clusterid', clusterId, userInfo.value);
    } catch (err) {
      defaultErrorHandler(err);
    } finally {
      isUserInfoFetching.value = false;
    }
  }

  async function downloadK10Logs() {
    if (isK10LogsPending.value) return;

    isK10LogsPending.value = true;
    try {
      const { data: runId } = await dashboardBffClient.supportLogs.triggerSupportLogs();
      if (runId) {
        const timeout = Date.now() + 10 * 60 * 1000; // 10 minute timeout;
        while (Date.now() < timeout) {
          await sleep(5000);

          try {
            const { data: file } = await dashboardBffClient.supportLogs.getSupportLogs(runId);
            downloadBlob(new Blob([file]), 'k10logs.zip');
            break;
          } catch (err) {
            if (err.response.status !== 404) {
              throw err;
            }
          }
        }
      }
    } catch (err) {
      showFlash({
        message:
          err.response && err.response.status === 500
            ? 'Unable to download logs'
            : 'Timed out downloading logs',
        messageType: 'danger',
        icon: 'icon-alert',
        dismiss: 'user',
      });
    }

    isK10LogsPending.value = false;
  }

  async function fetchLiveNamespaces(clusterName?: string) {
    try {
      namespaces.value = await fetchLiveNs(clusterName);
    } catch (e) {
      logError('fetchLiveNamespaces() err', e);
    }
  }

  return {
    actionDetailsCache,
    alertMessages,
    distributions,
    formPrefillData,
    healthStatus,
    namespaces,
    namespacesAllClusters,
    permsDetected,
    userInfo,
    isUserInfoFetching,
    policies,
    policiesByApp,
    protectedObjectCache,
    reports,
    restorePointsCache,
    restorePointArtifactsCache,
    transformEditorCache,
    settings,
    summaryMetrics,
    xhrEnabled,
    preventSidepanelClose,
    keylessAuth,
    isK10LogsPending,
    reportsIsEnabled,
    azureManagedIdentityStatus,
    setUserPermissionValue,
    getSetPoliciesByApp,
    fetchUserPermissions,
    downloadK10Logs,
    fetchLiveNamespaces,
  };
});

export async function fetchLiveNs(clusterName?: string) {
  const { data } = await dashboardBffClient.namespaces.listNamespaces(
    { live: true },
    { clusterId: clusterName },
  );
  if (data.loading) {
    await sleep(500);
    await fetchLiveNs(clusterName);
  }
  return data.namespaces ?? [];
}
